/**
 * AiChatToolCallBlock — Renders an inline collapsible tool call within an assistant message.
 *
 * Shows elapsed timer while running, result preview when collapsed.
 */

import { useState, useEffect } from 'react';
import { ChevronRight, CheckCircle2, AlertCircle } from 'lucide-react';
import type { AiChatToolCall } from '../types';
import { getToolMeta, getToolInlineSummary, getHumanToolName, isMcpTool } from '../utils/toolMeta';
import { formatToolInput, formatToolResult } from '../utils/toolFormatters';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type AiChatToolCallBlockProps = {
  toolCall: AiChatToolCall;
};

function useElapsedTimer(isRunning: boolean, startedAt?: number): string | null {
  const [elapsed, setElapsed] = useState<string | null>(null);

  useEffect(() => {
    if (!isRunning || !startedAt) {
      setElapsed(null);
      return;
    }
    const update = () => {
      const seconds = Math.floor((Date.now() - startedAt) / 1000);
      setElapsed(`${seconds}s`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [isRunning, startedAt]);

  return elapsed;
}

function getResultPreview(content: string): string {
  const firstLine = content.split('\n')[0] ?? '';
  return firstLine.length > 80 ? firstLine.slice(0, 80) + '...' : firstLine;
}

export function AiChatToolCallBlock({ toolCall }: AiChatToolCallBlockProps) {
  const [expanded, setExpanded] = useState(false);
  const meta = getToolMeta(toolCall.name);
  const humanName = getHumanToolName(toolCall.name);
  const isMcp = isMcpTool(toolCall.name);
  const summary = getToolInlineSummary(toolCall.name, toolCall.input);
  const elapsed = useElapsedTimer(toolCall.isRunning, toolCall.startedAt);

  return (
    <div
      className={`my-1 rounded-md border text-xs ${meta.color}`}
      data-testid="tool-call-block"
      data-tool-name={toolCall.name}
    >
      <button
        className="flex items-center gap-1.5 w-full px-2 py-1 text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <ChevronRight
          className={`h-3 w-3 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
        <span>{meta.icon}</span>
        <span className="font-medium truncate">{humanName}</span>
        {summary && (
          <span className="text-muted-foreground truncate ml-1">{summary}</span>
        )}
        <span className="ml-auto shrink-0 flex items-center gap-1">
          {toolCall.isRunning && (
            <>
              {elapsed && <span className="text-muted-foreground" data-testid="tool-call-elapsed">{elapsed}</span>}
              <RandomDotmSpinner size={12} />
            </>
          )}
          {!toolCall.isRunning && toolCall.result && !toolCall.result.isError && (
            <CheckCircle2 className="h-3 w-3 text-green-600" data-testid="tool-call-done" />
          )}
          {!toolCall.isRunning && toolCall.result?.isError && (
            <AlertCircle className="h-3 w-3 text-destructive" data-testid="tool-call-error" />
          )}
        </span>
      </button>

      {/* Result preview when collapsed — only for non-MCP tools (file reads, bash, etc.) */}
      {!expanded && !isMcp && !toolCall.isRunning && toolCall.result && (
        <div className="px-2 pb-1 text-[11px] text-muted-foreground truncate" data-testid="tool-call-result-preview">
          {getResultPreview(toolCall.result.content)}
        </div>
      )}

      {expanded && <ToolCallDetails toolCall={toolCall} isMcp={isMcp} />}
    </div>
  );
}

/** Expanded details panel — formatted for MCP tools, raw JSON for unknown tools. */
function ToolCallDetails({ toolCall, isMcp }: { toolCall: AiChatToolCall; isMcp: boolean }) {
  const inputFmt = formatToolInput(toolCall.name, toolCall.input);
  const resultFmt = toolCall.result
    ? formatToolResult(toolCall.name, toolCall.result.content, toolCall.result.isError)
    : null;

  return (
    <div className="px-2 pb-2 border-t text-[11px] space-y-1">
      {/* Raw MCP tool name */}
      {isMcp && (
        <div className="mt-1 text-muted-foreground font-mono">{toolCall.name}</div>
      )}
      <div className="font-medium mt-1">Input:</div>
      <pre className="whitespace-pre-wrap break-all bg-background/50 rounded p-1 max-h-40 overflow-auto">
        {inputFmt.formatted}
      </pre>
      {toolCall.result && resultFmt && (
        <>
          <div className="font-medium">
            {toolCall.result.isError ? 'Error:' : 'Result:'}
          </div>
          <pre className="whitespace-pre-wrap break-all bg-background/50 rounded p-1 max-h-40 overflow-auto">
            {resultFmt.formatted}
          </pre>
        </>
      )}
    </div>
  );
}
